/**
 * AB Test Authentication Integration Service
 * Handles Campus authentication integration and user role-based targeting
 */

import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ABTestDefinition, TargetingRule } from '../interfaces';

import { FeaturePermissions } from '@campus/dal';
import { AB_TEST_AUTH_SERVICE, AB_TEST_COOKIE_CONSENT_SERVICE, AB_TEST_PERMISSION_SERVICE } from '../tokens';
import { DebugLogger } from '../utils/debug-logger';

@Injectable({
  providedIn: 'root',
})
export class ABTestAuthIntegrationService {
  private cookieConsentService = inject(AB_TEST_COOKIE_CONSENT_SERVICE);
  private authService = inject(AB_TEST_AUTH_SERVICE);
  private permissionService = inject(AB_TEST_PERMISSION_SERVICE);
  private logger = inject(DebugLogger);

  constructor() {
    this.cookieConsentService.setInitialConsentValues();
  }

  /**
   * Check if user is eligible for AB testing based on cookie consent opt-out preferences
   * @returns Observable of boolean indicating eligibility
   */
  isUserEligibleForTesting(): Observable<boolean> {
    try {
      return combineLatest([
        this.cookieConsentService.cookies$,
        this.permissionService.hasPermission$(FeaturePermissions.TRACK_USER_JOURNEY),
      ]).pipe(
        map(([cookies, trackingPermission]) => {
          // !!cookies.statistics && trackingPermission
          return trackingPermission;
        }),
        catchError((error) => {
          this.logger.error('Failed to check user opt-out status. Did you provide the Cookie Consent Service?', error);
          return of(false);
        })
      );
    } catch (error) {
      this.logger.error('Failed to check user opt-out status. Did you provide the Cookie Consent Service?', error);
      return of(false);
    }
  }

  /**
   * Check if user meets targeting rules for a specific test
   * @param testDefinition - AB test definition with targeting rules
   * @returns Observable of boolean indicating if user meets targeting criteria
   */
  doesUserMeetTargetingRules(testDefinition: ABTestDefinition): Observable<boolean> {
    if (!testDefinition.targetingRules || testDefinition.targetingRules.length === 0) {
      return of(true); // No targeting rules means all users are eligible
    }

    return this.permissionService.getPermissionsDict$().pipe(
      map((permissionsDict) =>
        this.evaluateTargetingRules(testDefinition.targetingRules || [], { permissions: permissionsDict })
      ),
      catchError((error) => {
        this.logger.error('Failed to evaluate targeting rules:', error);
        // Default to not meeting criteria if we can't evaluate
        return of(false);
      })
    );
  }

  /**
   * Get current user ID for AB testing
   * @returns Observable of user ID as string
   */
  getUserId(): Observable<string> {
    return this.authService.getCurrent().pipe(
      map((user) => user.id.toString()),
      catchError((error) => {
        this.logger.error('Failed to get user ID:', error);
        throw error;
      })
    );
  }

  /**
   * Evaluate targeting rules against user context
   * @private
   */
  private evaluateTargetingRules(rules: TargetingRule[], userContext: Record<string, unknown>): boolean {
    if (rules.length === 0) {
      return true;
    }

    // Group rules by logical operator
    const andRules: TargetingRule[] = [];
    const orRules: TargetingRule[] = [];

    rules.forEach((rule) => {
      if (rule.logicalOperator === 'OR') {
        orRules.push(rule);
      } else {
        // Default to AND logic
        andRules.push(rule);
      }
    });

    // Evaluate AND rules (all must be true)
    const andResult = andRules.length === 0 || andRules.every((rule) => this.evaluateRule(rule, userContext));

    // Evaluate OR rules (at least one must be true)
    const orResult = orRules.length === 0 || orRules.some((rule) => this.evaluateRule(rule, userContext));

    // Both AND and OR conditions must be satisfied
    return andResult && orResult;
  }

  /**
   * Evaluate a single targeting rule
   * @private
   */
  private evaluateRule(rule: TargetingRule, userContext: Record<string, unknown>): boolean {
    const fieldValue = userContext[rule.field];

    // Special handling for permission-based rules (using Dictionary<boolean>)
    if (rule.field === 'permissions' && typeof fieldValue === 'object' && fieldValue !== null) {
      return this.evaluatePermissionRule(rule, fieldValue as Record<string, boolean>);
    }

    switch (rule.operator) {
      case 'equals':
        return fieldValue === rule.value;

      case 'in':
        return Array.isArray(rule.value) && rule.value.includes(fieldValue);

      case 'not_in':
        return Array.isArray(rule.value) && !rule.value.includes(fieldValue);

      case 'greater_than':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue > rule.value;

      case 'less_than':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue < rule.value;

      default:
        this.logger.warn(`Unknown targeting rule operator: ${rule.operator}`);
        return false;
    }
  }

  /**
   * Evaluate targeting rules specifically for user permissions (using Dictionary<boolean>)
   * @private
   */
  private evaluatePermissionRule(rule: TargetingRule, permissionsDict: Record<string, boolean>): boolean {
    switch (rule.operator) {
      case 'equals':
        // Check if user has the specific permission (permission exists and is true)
        return permissionsDict[String(rule.value)] === true;

      case 'in':
        // Check if user has any of the specified permissions
        if (Array.isArray(rule.value)) {
          return (rule.value as string[]).some((permission) => permissionsDict[permission] === true);
        }
        return permissionsDict[String(rule.value)] === true;

      case 'not_in':
        // Check if user does not have any of the specified permissions
        if (Array.isArray(rule.value)) {
          return !(rule.value as string[]).some((permission) => permissionsDict[permission] === true);
        }
        return permissionsDict[String(rule.value)] !== true;

      default:
        this.logger.warn(`Unsupported operator for permission rules: ${rule.operator}`);
        return false;
    }
  }
}
